import { parseArTaskAction } from '@erp/shared-types';

/**
 * The plain-language "why is this on my plate?" behind a thread reminder,
 * derived from the AR task's `arAction` payload.
 *
 * Kept as a structured descriptor rather than a pre-built sentence so the
 * copy stays in the component where gt-react's `<T>` can translate it — the
 * backend never ships user-facing prose for this banner.
 *
 * `unknown` is the graceful degradation: an action payload we can't parse (an
 * older shape, a kind added after this build) still gets a banner, it just
 * falls back to the task's own title/description for the wording.
 */
export type ThreadReminderReason =
  | {
      kind: 'reply_review';
      /** False when the drafting agent failed — there's nothing pre-written. */
      hasDraft: boolean;
    }
  | {
      kind: 'no_response';
      /** Whole days the outbound email had gone unanswered when flagged. */
      daysWaiting: number;
      counterpartyEmail: string | null;
      /** ISO timestamp of the unanswered send; null when unknown. */
      sentAt: string | null;
    }
  | { kind: 'unknown' };

/**
 * Read the reminder reason out of a task's metadata blob.
 *
 * Only the action kinds that actually point at a collection thread produce a
 * specific reason; everything else (including the org-wide statement/invoice
 * send queues, which carry no `threadId` and so should never reach a thread
 * banner) degrades to `unknown`.
 */
export function deriveThreadReminderReason(
  metadata: Record<string, unknown> | null | undefined,
): ThreadReminderReason {
  const action = parseArTaskAction(metadata);
  if (!action) return { kind: 'unknown' };

  switch (action.kind) {
    case 'ar_conversation_reply_review':
      return { kind: 'reply_review', hasDraft: action.draftId !== null };
    case 'ar_conversation_no_response':
      return {
        kind: 'no_response',
        daysWaiting: action.daysWaiting,
        counterpartyEmail: action.counterpartyEmail,
        sentAt: action.sentAt,
      };
    default:
      return { kind: 'unknown' };
  }
}
